import { useState } from 'react';
import { Link } from '../../router/Link';
import {
  Button,
  buttonClass,
  ConfirmButton,
  cx,
  Field,
  LoadingStatus,
  Menu,
  Notice,
  Panel,
  ProblemPanel,
  Spinner,
} from '../../ui';
import { GridSearch } from '../../listing/GridSearch';
import { SubscribeButton } from '../../subscriptions/SubscribeButton';
import { useCopy } from '../../strings/useCopy';
import { useManageSubscriptions, type FindState } from './useManageSubscriptions';
import { MANAGE_DONE_HREF, manageSortMenu } from './model';
import type { CandidateRow, ManageRow } from './model';
import layout from '../../ui/layout.module.css';
import styles from './channels.module.css';

/**
 * The manage-subscriptions page (`/feed/channels`). Renders props (R13) — every href arrived built,
 * every string arrived formatted, and the only branches are on state kinds.
 *
 * The add card sits above the list for the create-form's reason on the Playlists page: the two do
 * not depend on each other, and the thing a reader came to do should not wait behind a list.
 */
export function ManageSubscriptionsView() {
  const copy = useCopy().text;
  const words = copy.subscriptions.manage;
  const binding = useManageSubscriptions();
  const { list } = binding;

  return (
    <div className={layout.wide}>
      <section className={styles.page} aria-busy={list.loading ? true : undefined}>
        <header className={styles.head}>
          <div className={styles.headText}>
            <h1 className={styles.heading}>{words.heading}</h1>
            <p className={styles.lede}>{words.lede}</p>
          </div>
          {/* The way back — a link, not a Button: the feed is a place with a URL, and this
              page is its sub-page, reached from its Manage and returned from here. */}
          <Link className={buttonClass({ variant: 'default', size: 'sm' })} href={MANAGE_DONE_HREF}>
            {words.done}
          </Link>
        </header>

        <AddCard binding={binding} />

        {list.loading ? <LoadingStatus>{words.list.loading}</LoadingStatus> : null}

        {list.problem !== null ? <ProblemPanel copy={list.problem} onRetry={binding.retry} /> : null}

        {!list.loading && list.problem === null ? (
          <section className={styles.listSection} aria-label={words.list.following(list.count)}>
            <div className={styles.listHead}>
              <h2 className={styles.listHeading}>{words.list.following(list.count)}</h2>
              {/* Search then sort, on the row's right edge — the app-wide controls line
                  (CLAUDE.md): filters left, sort/order/search right. */}
              <GridSearch
                id="filter-subscriptions"
                value={binding.filter}
                onChange={binding.setFilter}
                words={{
                  label: words.list.filterLabel,
                  activeTitle: words.list.filterActiveTitle,
                  placeholder: words.list.filterPlaceholder,
                  clear: words.list.filterClear,
                  submit: words.list.filterDone,
                }}
              />
              <SortMenu binding={binding} />
            </div>

            {binding.removeProblem === null ? null : (
              <Notice tone="caution" live>
                <strong>{binding.removeProblem.title}.</strong> {binding.removeProblem.detail}
              </Notice>
            )}

            {list.count === 0 ? <p className={styles.empty}>{words.list.empty}</p> : null}
            {list.nothingMatches ? (
              <p className={styles.empty}>{words.list.noMatches(binding.filter.trim())}</p>
            ) : null}

            {list.rows.length === 0 ? null : (
              <ul className={styles.grid}>
                {list.rows.map((row) => (
                  <ChannelCard
                    key={row.channelId}
                    row={row}
                    removing={binding.removing.has(row.channelId)}
                    observeChannel={binding.observeChannel}
                    onRemove={binding.remove}
                  />
                ))}
              </ul>
            )}
          </section>
        ) : null}
      </section>
    </div>
  );
}

/** The sort, right of the search icon — display-only, the filter's reasoning. */
function SortMenu({ binding }: { readonly binding: ReturnType<typeof useManageSubscriptions> }) {
  const [open, setOpen] = useState(false);

  return (
    <Menu
      menu={manageSortMenu(binding.sort)}
      open={open}
      onOpenChange={setOpen}
      onSelect={(itemId) => {
        setOpen(false);
        // Narrowed by comparison rather than a cast — an id nobody declared falls back to default.
        binding.setSort(
          itemId === 'name-asc' ? 'name-asc' : itemId === 'name-desc' ? 'name-desc' : 'default',
        );
      }}
    />
  );
}

function AddCard({ binding }: { readonly binding: ReturnType<typeof useManageSubscriptions> }) {
  const words = useCopy().text.subscriptions.manage.add;

  return (
    <Panel as="section" ariaLabel={words.heading} className={styles.add}>
      <h2 className={styles.addHeading}>{words.heading}</h2>
      <div className={styles.addRow}>
        <Field
          id="add-subscription"
          label={words.label}
          hideLabel
          type="search"
          value={binding.addInput}
          onChange={binding.setAddInput}
          onSubmit={binding.find}
          placeholder={words.placeholder}
          clear={{ label: words.clear }}
          className={styles.addField}
        />
        <Button variant="primary" onClick={binding.find} disabled={binding.findState.kind === 'busy'}>
          {words.submit}
        </Button>
      </div>
      <FindResults state={binding.findState} />
    </Panel>
  );
}

function FindResults({ state }: { readonly state: FindState }) {
  const words = useCopy().text.subscriptions.manage.add;

  switch (state.kind) {
    case 'idle':
      return null;
    case 'busy':
      return (
        <div className={styles.finding} role="status">
          <Spinner />
          <span>{words.searching}</span>
        </div>
      );
    case 'nothing':
      return <p className={styles.findNote}>{words.nothing(state.q)}</p>;
    case 'not-resolvable':
      return <p className={styles.findNote}>{words.notResolvable}</p>;
    case 'problem':
      return (
        <Notice tone="caution" live>
          {state.detail}
        </Notice>
      );
    case 'results':
      return (
        <ul className={styles.candidates} aria-label={words.resultsLabel}>
          {state.rows.map((row) => (
            <Candidate key={row.channelId} row={row} />
          ))}
        </ul>
      );
  }
}

function Candidate({ row }: { readonly row: CandidateRow }) {
  const words = useCopy().text.subscriptions.manage;

  return (
    <li className={styles.row}>
      {row.avatarUrl === null ? (
        <span className={styles.avatarBlank} aria-hidden="true" />
      ) : (
        <img className={styles.avatar} src={row.avatarUrl} alt="" width={40} height={40} loading="lazy" />
      )}
      <span className={styles.rowText}>
        <Link className={styles.rowName} href={row.href} title={row.name}>
          {row.name}
          {row.verified ? <span className={styles.verified}> ✓</span> : null}
        </Link>
        <span className={styles.rowMeta}>
          {row.subscriberText === null ? null : (
            <span>{words.list.subscribers(row.subscriberText)}</span>
          )}
          {row.autoGenerated ? <span>{words.add.autoGenerated}</span> : null}
        </span>
      </span>
      <span className={styles.rowAction}>
        <SubscribeButton channelId={row.channelId} />
      </span>
    </li>
  );
}

/**
 * One subscription, as a tile — avatar over name, and its own Unsubscribe in the top-right corner
 * — the selection is retired, and a click on the tile always visits the channel now. The button is a
 * sibling *above* the link rather than inside it — a `<button>` nested in an `<a>` is invalid HTML —
 * and it confirms in place, red first, like every unsubscribe in the app.
 */
function ChannelCard({
  row,
  removing,
  observeChannel,
  onRemove,
}: {
  readonly row: ManageRow;
  readonly removing: boolean;
  readonly observeChannel: (channelId: string, element: Element | null) => void;
  readonly onRemove: (channelId: string) => void;
}) {
  const copy = useCopy().text;
  const words = copy.subscriptions.manage.list;

  return (
    <li
      className={cx(styles.card, row.gone && styles.cardGone)}
      ref={(element) => observeChannel(row.channelId, element)}
    >
      <span className={styles.remove}>
        <ConfirmButton
          size="sm"
          variant="ghost"
          icon
          label={removing ? words.removing : words.unsubscribeOne(row.name)}
          confirmLabel={copy.shell.confirm}
          confirmTitle={words.unsubscribeOne(row.name)}
          disabled={removing}
          onConfirm={() => onRemove(row.channelId)}
        >
          {removing ? <Spinner /> : <RemoveGlyph />}
        </ConfirmButton>
      </span>
      <Link
        className={styles.cardBody}
        href={row.href}
        title={row.gone ? words.goneTitle(row.name) : row.name}
      >
        {row.avatarUrl === null ? (
          // Initials, not a spinner: the name is known from the first response, so a card is never
          // waiting to say who it is — only what it looks like.
          <span className={styles.cardAvatarBlank} aria-hidden="true">
            {row.initials}
          </span>
        ) : (
          <img
            className={styles.cardAvatar}
            src={row.avatarUrl}
            alt=""
            width={72}
            height={72}
            loading="lazy"
          />
        )}
        <span className={styles.cardName}>{row.name}</span>
        {/* The one fact worth a tile's meta slot when it holds: the channel is gone from
            YouTube, so the button above is the only act left that means anything. */}
        {row.gone ? (
          <span className={styles.cardGoneNote}>{words.gone}</span>
        ) : row.subscriberText === null ? null : (
          <span className={styles.cardMeta}>{words.subscribers(row.subscriberText)}</span>
        )}
      </Link>
    </li>
  );
}

/** The history page's glyph, redrawn locally — `ui/icons.tsx` keeps only the shared set. */
function RemoveGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 12h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
